import type {
  RegistryAllocation,
  RegistryEntityContact,
  RegistryEntityDetail,
} from '../../api/registryEntityTypes';
import { allocationKey } from '../../entities/entitySource';
import type { Application, BeneficialOwner, Fund, Holder, HolderType } from '../types';

// Mock platform data in the admin API's shapes (GET /registry/entities/:id): one investment entity
// per sample holder and per new applicant, their linked contacts, and investor allocations. All
// names are fictional. Joint holdings are one entity with two or more Owner contacts; directors,
// trustees and partners are linked as Representatives, as the platform does today.

export const entityIdForHolder = (holderId: string) => `ie-${holderId}`;

/** The mock investor (contact in a fund opportunity) and entity behind a seeded application. */
export function seedApplicationLinks(
  applicationId: string,
  holderId?: string,
): Pick<Application, 'investmentEntityId' | 'allocationId'> {
  const investmentEntityId = holderId ? entityIdForHolder(holderId) : `ie-${applicationId}`;
  return {
    investmentEntityId,
    allocationId: allocationKey({
      investorExternalId: `inv-${applicationId}`,
      investmentEntityExternalId: investmentEntityId,
    }),
  };
}

/** Holders that have an allocation in the open Mezzanine raise but no application yet. */
const OPEN_ALLOCATIONS: { holderId: string; amount: string }[] = [
  { holderId: 'h-f001', amount: '150000.00' },
  { holderId: 'h-f002', amount: '250000.00' },
  { holderId: 'h-f003', amount: '100000.00' },
  { holderId: 'h-f004', amount: '500000.00' },
  { holderId: 'h-f005', amount: '75000.00' },
];
const OPEN_ALLOCATION_FUND_ID = 'f-mez3';

const REPRESENTATIVE_ROLE_LABELS: Record<BeneficialOwner['role'], string> = {
  DIRECTOR: 'Director',
  SHAREHOLDER: 'Shareholder',
  TRUSTEE: 'Trustee',
  BENEFICIARY: 'Beneficiary',
  APPOINTOR: 'Appointor',
  PARTNER: 'Partner',
};

const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
const digitsOnly = (value?: string) => (value ? value.replace(/\s/g, '') : null);

function contact(
  name: string,
  role: RegistryEntityContact['role'],
  email?: string,
  representativeType: string | null = null,
  otherRepresentativeType: string | null = null,
): RegistryEntityContact {
  return {
    externalId: `ct-${slug(name)}`,
    name,
    email: email ?? `${slug(name)}@example.com`,
    role,
    representativeType,
    otherRepresentativeType,
  };
}

/** "Theo & Clara Vasquez" → ["Theo Vasquez", "Clara Vasquez"]. */
function jointNames(name: string): string[] {
  const [first, rest] = name.split(' & ');
  const words = rest.split(' ');
  return [`${first} ${words[words.length - 1]}`, rest];
}

function nameFields(name: string, type: HolderType) {
  const blank = {
    firstName: null,
    lastName: null,
    companyName: null,
    trustName: null,
    trusteeName: null,
    trusteeType: null,
  };
  if (type === 'INDIVIDUAL') {
    const words = name.split(' ');
    return { ...blank, firstName: words.slice(0, -1).join(' '), lastName: words[words.length - 1] };
  }
  if (type === 'TRUST' || type === 'SMSF') {
    const [trusteeName, trustName] = name.split(' ATF ');
    return {
      ...blank,
      trusteeName,
      trustName: trustName ?? name,
      trusteeType: /Pty Ltd/.test(trusteeName) ? 'CORPORATE' : 'INDIVIDUAL',
    };
  }
  return { ...blank, companyName: name };
}

function holderContacts(holder: Holder): RegistryEntityContact[] {
  const joint = holder.type === 'INDIVIDUAL' && holder.beneficialOwners.length >= 2;
  const ownerNames = joint
    ? holder.beneficialOwners.map((owner) => owner.name)
    : [holder.contactName];
  const owners = ownerNames.map((name, index) =>
    contact(name, 'OWNER', index === 0 ? holder.email : undefined),
  );
  const seen = new Set(ownerNames);
  const representatives: RegistryEntityContact[] = [];
  if (!joint) {
    for (const person of holder.beneficialOwners) {
      if (seen.has(person.name)) continue;
      seen.add(person.name);
      representatives.push(
        contact(
          person.name,
          'REPRESENTATIVE',
          undefined,
          'OTHER',
          REPRESENTATIVE_ROLE_LABELS[person.role],
        ),
      );
    }
  }
  if (holder.adviser)
    representatives.push(contact(holder.adviser, 'REPRESENTATIVE', undefined, 'ADVISOR'));
  return [...owners, ...representatives];
}

function applicantContacts(application: Application): RegistryEntityContact[] {
  const joint =
    application.applicantType === 'INDIVIDUAL' && application.applicantName.includes(' & ');
  if (!joint) return [contact(application.contactName, 'OWNER', application.email)];
  return jointNames(application.applicantName).map((name, index) =>
    contact(name, 'OWNER', index === 0 ? application.email : undefined),
  );
}

function entity(
  externalId: string,
  name: string,
  type: HolderType,
  createdAt: string,
  extra: Pick<RegistryEntityDetail, 'abn' | 'acn' | 'isForeignInvestor' | 'contacts'>,
): RegistryEntityDetail {
  return {
    externalId,
    name,
    type,
    description: null,
    ...nameFields(name, type),
    ...extra,
    createdAt,
    updatedAt: createdAt,
    allocations: [],
  };
}

function allocation(
  investorExternalId: string,
  owner: RegistryEntityDetail,
  fund: Fund | undefined,
  amount: string,
  createdAt: string,
): RegistryAllocation {
  const ownerContact = owner.contacts.find((item) => item.role === 'OWNER') ?? owner.contacts[0];
  return {
    investorExternalId,
    investorStatus: 'COMMITTED_INVESTOR',
    investmentEntityExternalId: owner.externalId,
    investmentEntityName: owner.name,
    investmentEntityType: owner.type,
    contact: { externalId: ownerContact.externalId, name: ownerContact.name },
    fundOpportunity: {
      externalId: `fo-${fund?.id ?? 'unknown'}`,
      name: fund?.name ?? 'Sample fund opportunity',
      status: fund?.status === 'RAISING' ? 'APPLICATION_OPEN' : 'UNDER_MANAGEMENT',
    },
    allocatedAmount: amount,
    createdAt,
    updatedAt: createdAt,
  };
}

export function buildPlatformEntities(
  holders: Holder[],
  applications: Application[],
  funds: Fund[],
): RegistryEntityDetail[] {
  const fundById = new Map(funds.map((fund) => [fund.id, fund]));
  const entities = new Map<string, RegistryEntityDetail>();
  for (const holder of holders) {
    entities.set(
      holder.investmentEntityId,
      entity(holder.investmentEntityId, holder.name, holder.type, holder.createdAt, {
        abn: digitsOnly(holder.abn),
        acn: digitsOnly(holder.acn),
        isForeignInvestor: holder.residency !== 'AU',
        contacts: holderContacts(holder),
      }),
    );
  }
  for (const application of applications) {
    const owner =
      entities.get(application.investmentEntityId) ??
      entity(
        application.investmentEntityId,
        application.applicantName,
        application.applicantType,
        application.createdAt,
        {
          abn: null,
          acn: null,
          isForeignInvestor: false,
          contacts: applicantContacts(application),
        },
      );
    owner.allocations.push(
      allocation(
        `inv-${application.id}`,
        owner,
        fundById.get(application.fundId),
        application.amount.toFixed(2),
        application.createdAt,
      ),
    );
    entities.set(owner.externalId, owner);
  }
  for (const open of OPEN_ALLOCATIONS) {
    const owner = entities.get(entityIdForHolder(open.holderId));
    if (!owner) continue;
    owner.allocations.push(
      allocation(
        `inv-${open.holderId}-mez3`,
        owner,
        fundById.get(OPEN_ALLOCATION_FUND_ID),
        open.amount,
        '2026-10-01',
      ),
    );
  }
  return Array.from(entities.values());
}
